/**
 * Per-user localStorage key helper.
 *
 * Appends `_{hashedId}` to storage keys so that multiple accounts
 * sharing the same browser don't overwrite each other's preferences.
 * The user ID is hashed (not stored raw) to avoid exposing it in DevTools.
 *
 * Keys that remain global (no suffix): nizus_auth_user, nizus_auth_token, theme, nizus_language.
 */

/** Simple djb2 hash → short base-36 string (e.g. "x4f2k") */
function hashId(id) {
    const str = String(id);
    let h = 5381;
    for (let i = 0; i < str.length; i++) {
        h = ((h << 5) + h + str.charCodeAt(i)) & 0xffffffff;
    }
    return (h >>> 0).toString(36);
}

/**
 * Decode the stored user data (base64-encoded JSON).
 * Falls back to plain JSON for migration from pre-obfuscation format.
 */
export function decodeStoredUser(raw) {
    if (!raw) return null;
    try {
        return JSON.parse(atob(raw));
    } catch {
        // Fallback: plain JSON from before obfuscation
        try { return JSON.parse(raw); } catch { return null; }
    }
}

/** Encode user data to base64 for localStorage storage. */
export function encodeStoredUser(data) {
    return btoa(JSON.stringify(data));
}

function getUserId() {
    try {
        const raw = localStorage.getItem('nizus_auth_user');
        const parsed = decodeStoredUser(raw);
        return parsed?.id ?? null;
    } catch {
        return null;
    }
}

// Track whether migration has run for this session (per raw ID)
const migratedIds = new Set();

/**
 * One-time migration: rename `baseKey_{rawId}` → `baseKey_{hashedId}`
 * for all known per-user keys in localStorage.
 */
function migrateIfNeeded(rawId, hashed) {
    if (migratedIds.has(rawId)) return;
    migratedIds.add(rawId);

    const suffix = `_${rawId}`;
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
        keys.push(localStorage.key(i));
    }
    for (const key of keys) {
        if (key.endsWith(suffix)) {
            const base = key.slice(0, -suffix.length);
            const newKey = `${base}_${hashed}`;
            // Only migrate if new key doesn't already exist
            if (localStorage.getItem(newKey) === null) {
                localStorage.setItem(newKey, localStorage.getItem(key));
            }
            localStorage.removeItem(key);
        }
    }
}

/**
 * Return a per-user storage key: `baseKey_{hashedId}`.
 * Falls back to `baseKey` if no user is logged in.
 */
export function userKey(baseKey) {
    const uid = getUserId();
    if (!uid) return baseKey;
    const hashed = hashId(uid);
    migrateIfNeeded(uid, hashed);
    return `${baseKey}_${hashed}`;
}
